import { useState, useEffect, useMemo, useRef } from 'react';
import { Check, ChevronsUpDown, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { useCustomerTopProducts } from '@/hooks/useCustomerTopProducts';
import { searchProductIdsFuzzy, searchServiceIdsFuzzy } from '@/lib/productSearch'; // A-quattuor.6 F1-d

interface ProductService {
  id: string;
  name: string;
  type: 'product' | 'service';
  sale_price?: number;
  sku?: string;
  stock_quantity?: number;
  rental_stock_quantity?: number;
  reserved_stock_quantity?: number;
  product_type?: string;
}

interface ProductServiceComboboxProps {
  value: string;
  onChange: (value: string, item: ProductService) => void;
  type: 'product' | 'service';
  error?: string;
  onProductSelected?: (product: ProductService) => void;
  /** L-VEN.1c: serviço escolhido abre o modal de quantidade/preço/desconto (mesma via do onProductSelected) */
  onServiceSelected?: (service: ProductService) => void;
  categoryFilter?: string[];
  /** L-VEN.1: cliente do pedido — habilita o grupo "Mais comprados por este cliente" (só produto, sem busca) */
  customerId?: string;
  /** m-VENDAS (I.177): linha recém-criada por "Adicionar Item" — abre a busca do produto ao montar (foco na busca) */
  autoOpen?: boolean;
}

/** L-VEN.1 (D-VEN.1): item do histórico com a contagem de compras para o selo */
type TopProduct = ProductService & { sale_count: number };

const HIST_PREFIX = 'hist-';

export function ProductServiceCombobox({ value, onChange, type, error, onProductSelected, onServiceSelected, categoryFilter, customerId, autoOpen = false }: ProductServiceComboboxProps) {
  const { companyId } = useMultiTenant();
  const [open, setOpen] = useState(false);

  // m-VENDAS (I.177): só na montagem da linha nova — abre a busca e põe o cursor nela na primeira abertura; depois
  // que fecha, o clique normal segue como antes (sem foco automático)
  const [focusSearchOnce, setFocusSearchOnce] = useState(autoOpen);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (autoOpen) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (wasOpenRef.current && !open) setFocusSearchOnce(false);
    wasOpenRef.current = open;
  }, [open]);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<ProductService[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItemData, setSelectedItemData] = useState<ProductService | null>(null);
  // L-VEN.2: ids devolvidos pela busca fuzzy para o termo corrente — alimenta o grupo "Já comprados"
  const [searchMatch, setSearchMatch] = useState<{ term: string; ids: string[] } | null>(null);

  const PAGE_SIZE = 50;

  // L-VEN.1 (D-VEN.1): top 5 do histórico do cliente — só com cliente, só produto, só sem texto de
  // busca. Lista principal, paginação e busca não mudam; o grupo é prefixado e some fora dessas condições.
  // L-DRV.1: a query vive no hook único useCustomerTopProducts (mesmo critério no app do motorista).
  const topProductsEnabled = type === 'product' && !!companyId && !!customerId && !search.trim();
  const { data: topProductRows } = useCustomerTopProducts(companyId, customerId, topProductsEnabled);
  const topProducts = useMemo<TopProduct[]>(
    () => (topProductRows ?? []).map(p => ({
      id: p.id,
      name: p.name,
      type: 'product' as const,
      sale_price: p.sale_price,
      sku: p.sku,
      stock_quantity: p.stock_quantity,
      rental_stock_quantity: p.rental_stock_quantity,
      reserved_stock_quantity: p.reserved_stock_quantity,
      product_type: p.product_type,
      sale_count: p.sale_count,
    })),
    [topProductRows],
  );
  const showTopProducts = topProductsEnabled && topProducts.length > 0;

  // L-VEN.2 (D-VEN.6): com texto de busca, "Já comprados" = produtos já comprados pelo cliente DENTRO
  // dos ids que a busca fuzzy devolveu (searchMatch do termo corrente). Mesmo hook, cacheKey = termo.
  // Os itens seguem aparecendo também na lista principal.
  const purchasedEnabled = type === 'product' && !!companyId && !!customerId && !!search.trim()
    && searchMatch?.term === search.trim() && searchMatch.ids.length > 0;
  const { data: purchasedRows } = useCustomerTopProducts(companyId, customerId, purchasedEnabled, {
    productIds: searchMatch?.ids ?? null,
    cacheKey: search.trim(),
    limit: 10,
    categoryFilter,
  });
  const purchasedProducts = useMemo<TopProduct[]>(
    () => (purchasedRows ?? []).map(p => ({
      id: p.id,
      name: p.name,
      type: 'product' as const,
      sale_price: p.sale_price,
      sku: p.sku,
      stock_quantity: p.stock_quantity,
      rental_stock_quantity: p.rental_stock_quantity,
      reserved_stock_quantity: p.reserved_stock_quantity,
      product_type: p.product_type,
      sale_count: p.sale_count,
    })),
    [purchasedRows],
  );
  const showPurchased = purchasedEnabled && purchasedProducts.length > 0;

  // L-VEN.1: seleção segue a MESMA via da lista normal (onProductSelected ou onChange)
  const selectItem = (selectedItem: ProductService) => {
    // L-VEN.1c: serviço com callback abre o modal; estado visual só quando o modal confirmar
    if (onServiceSelected && type === 'service') {
      onServiceSelected(selectedItem);
      setOpen(false);
      return;
    }
    if (onProductSelected && type === 'product') {
      onProductSelected(selectedItem);
      setOpen(false);
      return;
    }
    setSelectedItemData(selectedItem);
    onChange(selectedItem.id, selectedItem);
    setOpen(false);
    setSearch('');
  };

  useEffect(() => {
    const fetchItems = async () => {
      if (!companyId) return;
      setLoading(true);
      
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      
      if (type === 'product') {
        // Buscar produtos unificados
        let query = supabase
          .from('products')
          .select('id, name, sku, sale_price, stock_quantity, rental_stock_quantity, reserved_stock_quantity, product_type', { count: page === 0 ? 'exact' : undefined })
          .eq('company_id', companyId)
          .eq('active', true);

        // Aplicar filtro de categorias se fornecido
        if (categoryFilter && categoryFilter.length > 0) {
          query = query.in('category', categoryFilter);
        }

        // A-quattuor.6 F1-d: nome/SKU/EAN via RPC search_products_fuzzy (sem acento, AND por palavra)
        // → ids; select/.range/count/order seguem no PostgREST.
        if (search.trim()) {
          try {
            const ids = await searchProductIdsFuzzy(companyId, search);
            // L-VEN.2: guarda os ids do termo para o grupo "Já comprados"
            setSearchMatch({ term: search.trim(), ids });
            query = query.in('id', ids);
          } catch (rpcError) {
            console.error('Erro ao buscar produtos:', rpcError);
            setLoading(false);
            return;
          }
        } else {
          setSearchMatch(null);
        }

        const { data, error, count } = await query.order('name').range(from, to);
        
        if (error) {
          console.error('Erro ao buscar produtos:', error);
        }
        
        if (!error && data) {
          const mappedData = data.map((p: any) => ({
            id: p.id,
            name: p.name,
            type: 'product' as const,
            sale_price: p.sale_price,
            sku: p.sku,
            stock_quantity: p.stock_quantity,
            rental_stock_quantity: p.rental_stock_quantity,
            reserved_stock_quantity: p.reserved_stock_quantity,
            product_type: p.product_type
          }));
          
          if (page === 0) {
            setItems(mappedData);
            setTotalCount(count || 0);
          } else {
            setItems(prev => [...prev, ...mappedData]);
          }
          setHasMore(data.length === PAGE_SIZE);
        }
      } else {
        // Buscar serviços
        let query = supabase
          .from('services')
          .select('id, service_name, sale_price', { count: page === 0 ? 'exact' : undefined })
          .eq('company_id', companyId)
          .eq('active', true);

        // A-quattuor.6 F1-d: service_name via RPC search_services_fuzzy (sem acento, AND por palavra)
        // → ids; select/.range/count/order seguem no PostgREST.
        if (search.trim()) {
          try {
            const ids = await searchServiceIdsFuzzy(companyId, search);
            query = query.in('id', ids);
          } catch (rpcError) {
            console.error('Erro ao buscar serviços:', rpcError);
            setLoading(false);
            return;
          }
        }

        const { data, error, count } = await query.order('service_name').range(from, to);
        
        if (error) {
          console.error('Erro ao buscar serviços:', error);
        }
        
        if (!error && data) {
          const mappedData = data.map(s => ({
            id: s.id,
            name: s.service_name,
            type: 'service' as const,
            sale_price: s.sale_price
          }));
          
          if (page === 0) {
            setItems(mappedData);
            setTotalCount(count || 0);
          } else {
            setItems(prev => [...prev, ...mappedData]);
          }
          setHasMore(data.length === PAGE_SIZE);
        }
      }
      setLoading(false);
    };

    const timer = setTimeout(fetchItems, 300);
    return () => clearTimeout(timer);
  }, [search, companyId, type, page, categoryFilter]);

  useEffect(() => {
    setPage(0);
    setItems([]);
    setHasMore(true);
  }, [search, type, categoryFilter]);

  // Buscar item selecionado se não estiver na lista
  useEffect(() => {
    const fetchSelectedItem = async () => {
      if (!value || !companyId) {
        setSelectedItemData(null);
        return;
      }

      // Verificar se já está na lista
      const foundInList = items.find((i) => i.id === value);
      if (foundInList) {
        setSelectedItemData(foundInList);
        return;
      }

      // Buscar o item específico
      try {
        if (type === 'product') {
          const { data, error } = await supabase
            .from('products')
            .select('id, name, sku, sale_price, stock_quantity, rental_stock_quantity, reserved_stock_quantity')
            .eq('id', value)
            .eq('company_id', companyId)
            .eq('active', true)
            .single();

          if (!error && data) {
            setSelectedItemData({
              id: data.id,
              name: data.name,
              type: 'product',
              sale_price: data.sale_price,
              sku: data.sku,
              stock_quantity: data.stock_quantity,
              rental_stock_quantity: data.rental_stock_quantity,
              reserved_stock_quantity: data.reserved_stock_quantity
            });
          }
        } else {
          const { data, error } = await supabase
            .from('services')
            .select('id, service_name, sale_price')
            .eq('id', value)
            .eq('company_id', companyId)
            .eq('active', true)
            .single();

          if (!error && data) {
            setSelectedItemData({
              id: data.id,
              name: data.service_name,
              type: 'service',
              sale_price: data.sale_price
            });
          }
        }
      } catch (error) {
        console.error('Erro ao buscar item selecionado:', error);
      }
    };

    fetchSelectedItem();
  }, [value, type, companyId, items]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const bottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 1;
    
    if (bottom && hasMore && !loading) {
      setPage(prev => prev + 1);
    }
  };

  const selectedItem = selectedItemData || items.find((i) => i.id === value);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between",
              error && "border-destructive"
            )}
          >
            {selectedItem
              ? selectedItem.name
              : `Selecione o ${type === 'product' ? 'produto' : 'serviço'}...`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-[400px] p-0 z-[9999] bg-background pointer-events-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Buscar ${type === 'product' ? 'produto' : 'serviço'}...`}
              value={search}
              onValueChange={setSearch}
              autoFocus={focusSearchOnce}
            />
            <CommandList onScroll={handleScroll}>
              <CommandEmpty>
                {loading ? "Carregando..." : `Nenhum ${type === 'product' ? 'produto' : 'serviço'} encontrado.`}
              </CommandEmpty>
              {/* L-VEN.1: top 5 do histórico do cliente — value prefixado para não colidir com a lista */}
              {showTopProducts && (
                <CommandGroup
                  heading={
                    <span className="flex items-center gap-1 text-sky-600">
                      <Star className="h-3 w-3 fill-current" />
                      Mais comprados
                    </span>
                  }
                >
                  {topProducts.map((item) => (
                    <CommandItem
                      key={`${HIST_PREFIX}${item.id}`}
                      value={`${HIST_PREFIX}${item.id}`}
                      onSelect={(selectedValue) => {
                        const realId = selectedValue.startsWith(HIST_PREFIX)
                          ? selectedValue.slice(HIST_PREFIX.length)
                          : selectedValue;
                        const selected = topProducts.find(p => p.id === realId);
                        if (selected) selectItem(selected);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === item.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{item.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {item.sku && `SKU: ${item.sku} • `}
                          {item.sale_price && `R$ ${item.sale_price.toFixed(2)}`}
                          {item.stock_quantity !== undefined && ` • Estoque: ${item.stock_quantity}`}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {/* L-VEN.2 (D-VEN.6): com texto de busca, já comprados pelo cliente dentro do resultado — value prefixado */}
              {showPurchased && (
                <CommandGroup
                  heading={
                    <span className="flex items-center gap-1 text-sky-600">
                      <Star className="h-3 w-3 fill-current" />
                      Já comprados
                    </span>
                  }
                >
                  {purchasedProducts.map((item) => (
                    <CommandItem
                      key={`${HIST_PREFIX}${item.id}`}
                      value={`${HIST_PREFIX}${item.id}`}
                      onSelect={(selectedValue) => {
                        const realId = selectedValue.startsWith(HIST_PREFIX)
                          ? selectedValue.slice(HIST_PREFIX.length)
                          : selectedValue;
                        const selected = purchasedProducts.find(p => p.id === realId);
                        if (selected) selectItem(selected);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === item.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span>{item.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {item.sku && `SKU: ${item.sku} • `}
                          {item.sale_price && `R$ ${item.sale_price.toFixed(2)}`}
                          {item.stock_quantity !== undefined && ` • Estoque: ${item.stock_quantity}`}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {/* D-VEN.2: com o grupo de histórico, separador + título na lista geral; sem ele, lista como sempre */}
              {(showTopProducts || showPurchased) && <CommandSeparator />}
              <CommandGroup heading={(showTopProducts || showPurchased) ? 'Produtos' : undefined}>
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
              onSelect={(selectedValue) => {
                const selectedItem = items.find(i => i.id === selectedValue);
                
                if (selectedItem) {
                  // L-VEN.1c: serviço com callback abre o modal; estado visual só quando o modal confirmar
                  if (onServiceSelected && type === 'service') {
                    onServiceSelected(selectedItem);
                    setOpen(false);
                    return;
                  }
                  // Se tem callback onProductSelected, usar ele (para abrir modal)
                  // NÃO atualiza o selectedItemData ainda - só quando modal confirmar
                  if (onProductSelected && type === 'product') {
                    onProductSelected(selectedItem);
                    setOpen(false);
                    // Não atualiza o estado visual aqui!
                    return;
                  }
                  
                  // Comportamento padrão (sem modal)
                  setSelectedItemData(selectedItem);
                  onChange(selectedItem.id, selectedItem);
                  setOpen(false);
                  // Limpar busca para próxima seleção
                  setSearch('');
                }
              }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === item.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{item.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.sku && `SKU: ${item.sku} • `}
                        {item.sale_price && `R$ ${item.sale_price.toFixed(2)}`}
                        {item.stock_quantity !== undefined && ` • Estoque: ${item.stock_quantity}`}
                      </span>
                    </div>
                  </CommandItem>
                ))}
                {loading && page > 0 && (
                  <div className="py-2 text-center text-sm text-muted-foreground">
                    Carregando mais...
                  </div>
                )}
                {!loading && items.length > 0 && totalCount > 0 && (
                  <div className="py-2 text-center text-xs text-muted-foreground">
                    Mostrando {items.length} de {totalCount} {type === 'product' ? 'produtos' : 'serviços'}
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
}
